import { useEffect, useState } from "react";
import { UserPlus, Copy, Check, Loader2, Trash2, Send } from "lucide-react";
import { CoordinatorLayout } from "@/components/layout/coordinator-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface InvitationRow {
  id: number;
  patientName: string;
  patientFirstName: string | null;
  patientLastName: string | null;
  patientEmail: string;
  welcomeNote: string | null;
  status: "pending" | "accepted" | "cancelled" | "expired";
  expiresAt: string;
  createdAt: string;
  acceptedAt: string | null;
  lastSentAt: string | null;
}

interface CreateResponse extends InvitationRow {
  acceptPath: string;
  hasExistingAccount: boolean;
  emailError: string | null;
}

const STATUS_STYLE: Record<InvitationRow["status"], string> = {
  pending: "bg-amber-100 text-amber-800",
  accepted: "bg-emerald-100 text-emerald-800",
  expired: "bg-slate-100 text-slate-600",
  cancelled: "bg-slate-100 text-slate-500 line-through",
};

export default function CoordinatorInvitations() {
  const [rows, setRows] = useState<InvitationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patientFirstName, setPatientFirstName] = useState("");
  const [patientLastName, setPatientLastName] = useState("");
  const [patientEmail, setPatientEmail] = useState("");
  const [dob, setDob] = useState("");
  const [welcomeNote, setWelcomeNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<CreateResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    fetch(`${BASE}/api/coordinator/invitations`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setRows(d as InvitationRow[]))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    setLastResult(null);
    setCopied(false);
    try {
      const res = await fetch(`${BASE}/api/coordinator/invitations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          patientFirstName,
          patientLastName,
          patientEmail,
          dob: dob || undefined,
          welcomeNote: welcomeNote || undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as CreateResponse;
      setPatientFirstName("");
      setPatientLastName("");
      setPatientEmail("");
      setDob("");
      setWelcomeNote("");
      setLastResult(data);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send invitation");
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id: number) => {
    setBusyId(id);
    try {
      await fetch(`${BASE}/api/coordinator/invitations/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      load();
    } finally {
      setBusyId(null);
    }
  };

  const resend = async (id: number) => {
    setBusyId(id);
    setError(null);
    try {
      const res = await fetch(`${BASE}/api/coordinator/invitations/${id}/resend`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend invitation");
    } finally {
      setBusyId(null);
    }
  };

  const lastLink = lastResult
    ? `${window.location.origin}${lastResult.acceptPath}`
    : null;

  const copyLink = async () => {
    if (!lastLink) return;
    await navigator.clipboard.writeText(lastLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <CoordinatorLayout>
      <div className="p-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-2">
          <UserPlus className="h-6 w-6 text-sky-600" />
          <h1 className="text-2xl font-bold text-slate-900">Patient invitations</h1>
        </div>
        <p className="text-slate-600 mb-6">
          Invite a named patient to join your practice on RheumHub. We email them a
          secure, single-use link valid for 7 days.
        </p>

        <form
          onSubmit={submit}
          className="bg-white p-5 rounded-2xl border border-slate-200 mb-6"
          data-testid="invite-form"
        >
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label htmlFor="invite-first-name" className="text-xs font-medium text-slate-700 mb-1 block">
                First name
              </label>
              <Input
                id="invite-first-name"
                value={patientFirstName}
                onChange={(e) => setPatientFirstName(e.target.value)}
                required
                maxLength={100}
                placeholder="e.g. Jane"
                data-testid="invite-first-name"
              />
            </div>
            <div>
              <label htmlFor="invite-last-name" className="text-xs font-medium text-slate-700 mb-1 block">
                Last name
              </label>
              <Input
                id="invite-last-name"
                value={patientLastName}
                onChange={(e) => setPatientLastName(e.target.value)}
                required
                maxLength={100}
                placeholder="e.g. Smith"
                data-testid="invite-last-name"
              />
            </div>
            <div>
              <label htmlFor="invite-email" className="text-xs font-medium text-slate-700 mb-1 block">
                Patient email
              </label>
              <Input
                id="invite-email"
                type="email"
                value={patientEmail}
                onChange={(e) => setPatientEmail(e.target.value)}
                required
                maxLength={320}
                placeholder="patient@example.com"
                data-testid="invite-email"
              />
            </div>
            <div>
              <label htmlFor="invite-dob" className="text-xs font-medium text-slate-700 mb-1 block">
                Date of birth
                <span className="ml-1 text-slate-400 font-normal">(identity check)</span>
              </label>
              <Input
                id="invite-dob"
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                data-testid="invite-dob"
              />
            </div>
          </div>
          <div className="mb-3">
            <label htmlFor="invite-welcome-note" className="text-xs font-medium text-slate-700 mb-1 block">
              Welcome note <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="invite-welcome-note"
              className="w-full text-sm rounded-md border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-200"
              rows={3}
              value={welcomeNote}
              onChange={(e) => setWelcomeNote(e.target.value)}
              maxLength={1000}
              placeholder="A short personal note from the practice — appears in the invitation email."
              data-testid="invite-welcome-note"
            />
          </div>
          <Button
            type="submit"
            disabled={
              submitting ||
              !patientFirstName.trim() ||
              !patientLastName.trim() ||
              !patientEmail.trim()
            }
            data-testid="invite-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…
              </>
            ) : (
              "Send invitation"
            )}
          </Button>
        </form>

        {lastResult && (
          <div
            className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-6"
            data-testid="invite-success"
          >
            <p className="text-sm font-semibold text-emerald-900 mb-1">
              Invitation sent to {lastResult.patientEmail}
            </p>
            <p className="text-xs text-emerald-800 mb-2">
              {lastResult.hasExistingAccount
                ? "This email already has a RheumHub account — the patient will be asked to sign in and link your practice to their profile."
                : "The patient will receive a branded email with a secure registration link. The link expires in 7 days."}
            </p>
            {lastResult.emailError && (
              <p className="text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded-md px-2 py-1.5 mb-2">
                Email delivery reported an error: {lastResult.emailError}. You can also copy the
                link below and share it via another secure channel.
              </p>
            )}
            {lastLink && (
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white border border-emerald-200 rounded-lg px-2 py-1.5 truncate">
                  {lastLink}
                </code>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={copyLink}
                  data-testid="invite-copy"
                  aria-label="Copy invitation link"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            )}
          </div>
        )}

        {error && (
          <p
            role="alert"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4"
          >
            {error}
          </p>
        )}

        <h2 className="text-sm font-semibold text-slate-900 mb-2">Recent invitations</h2>
        {loading && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-2xl p-6 text-center">
            No invitations yet.
          </p>
        )}
        <div className="space-y-2">
          {rows.map((r) => (
            <div
              key={r.id}
              className="bg-white p-3 rounded-xl border border-slate-200 flex items-center gap-3"
              data-testid={`invite-row-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {r.patientName}
                </p>
                <p className="text-xs text-slate-500 truncate">{r.patientEmail}</p>
              </div>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status]}`}
              >
                {r.status}
              </span>
              <span className="text-xs text-slate-500 hidden sm:inline">
                {new Date(r.createdAt).toLocaleDateString("en-GB")}
              </span>
              {(r.status === "pending" || r.status === "expired") && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={busyId === r.id}
                  onClick={() => resend(r.id)}
                  data-testid={`invite-resend-${r.id}`}
                  aria-label="Resend invitation"
                  title="Resend with a fresh 7-day link"
                >
                  {busyId === r.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              )}
              {(r.status === "pending" || r.status === "expired") && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={busyId === r.id}
                  onClick={() => cancel(r.id)}
                  data-testid={`invite-cancel-${r.id}`}
                  aria-label="Cancel invitation"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </CoordinatorLayout>
  );
}
